import { useEffect, useState, type KeyboardEvent } from 'react'
import * as api from 'api/entityApi'

/**
 * Replacement for the bootstrap-tokenfield tag box (`tokenLeadAddTag`),
 * including its typeahead — suggestions come from
 * `UserCRM/GetTagListWithFilter` (legacy `funcToDisplayTagListWithFilter`).
 */
export function TagInput({
  label,
  tags,
  onChange,
  placeholder = 'Type a tag and press Enter',
}: {
  label: string
  tags: string[]
  onChange: (tags: string[]) => void
  placeholder?: string
}) {
  const [draft, setDraft] = useState('')
  const [suggestions, setSuggestions] = useState<string[]>([])

  // Debounced lookup, so typing does not fire a request per keystroke.
  useEffect(() => {
    const term = draft.trim()
    if (term.length < 2) {
      setSuggestions([])
      return
    }

    const controller = new AbortController()
    let active = true
    const timer = setTimeout(() => {
      api
        .fetchTagSuggestions(term, controller.signal)
        .then((result) => {
          if (active) setSuggestions(result.filter((s) => !tags.includes(s)).slice(0, 8))
        })
        .catch((err: Error) => {
          if (err.name !== 'AbortError') setSuggestions([])
        })
    }, 300)

    return () => {
      active = false
      clearTimeout(timer)
      controller.abort()
    }
  }, [draft, tags])

  const commit = () => {
    const value = draft.trim()
    if (!value) return
    if (!tags.includes(value)) onChange([...tags, value])
    setDraft('')
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      commit()
    } else if (e.key === 'Backspace' && !draft && tags.length > 0) {
      onChange(tags.slice(0, -1))
    }
  }

  return (
    <div className="form-group">
      <label>{label}</label>
      <div className="tag-input">
        {tags.map((tag) => (
          <span key={tag} className="tag-token">
            {tag}
            <button type="button" onClick={() => onChange(tags.filter((t) => t !== tag))} title={`Remove ${tag}`}>
              ✕
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commit}
        />
      </div>

      {suggestions.length > 0 && (
        <div className="tag-suggestions">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              className="tag-suggestion"
              // onMouseDown fires before the input's onBlur, so the click is not
              // swallowed by the blur-commit.
              onMouseDown={(e) => {
                e.preventDefault()
                if (!tags.includes(suggestion)) onChange([...tags, suggestion])
                setDraft('')
                setSuggestions([])
              }}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
